/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Display formatting for the
 * forecast pages.
 */
import { daysAgo } from '@/demo/procurement/data/dates';
import {
  displayQty,
  displayUnit,
  fmtMoneyCompact,
} from '@/demo/procurement/data/format';
import type { Measure } from './forecast-types';

const QTY = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const QTY_ONE_DECIMAL = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

/** Whole numbers, with one decimal for the small fractional ones. */
export function fmtQty(value: number): string {
  if (Number.isInteger(value) || Math.abs(value) >= 100) {
    return QTY.format(value);
  }
  return QTY_ONE_DECIMAL.format(value);
}

/** Axis ticks abbreviate: 12,400 reads as 12.4k. */
export function fmtAxisQty(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${QTY_ONE_DECIMAL.format(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${QTY_ONE_DECIMAL.format(value / 1_000)}k`;
  return QTY.format(value);
}

export function fmtMeasure(value: number, measure: Measure): string {
  return measure === 'cost' ? fmtMoneyCompact(value) : fmtQty(value);
}

const DATE_TIME = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function fmtDateTime(date: Date): string {
  return DATE_TIME.format(date);
}

export function fmtIsoDateTime(iso: string): string {
  return DATE_TIME.format(new Date(iso));
}

/** The nightly run starts at 2:10 AM. */
export function runStart(startedDaysAgo: number): Date {
  const start = daysAgo(startedDaysAgo);
  start.setHours(2, 10, 0, 0);
  return start;
}

export function fmtDuration(seconds: number): string {
  if (seconds < 90) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
}

export function fmtShare(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/** Units that read the same in the singular and plural. */
const INVARIABLE_UNITS = new Set(['C', 'each']);

/** A quantity as shown: wire in feet, everything else unchanged. */
export function fmtQtyIn(value: number, unit: string): string {
  return fmtQty(displayQty(value, unit));
}

/** A table cell: wire keeps its unit ('400 ft') so a bare 400 is never ambiguous. */
export function fmtCellQty(value: number, unit: string): string {
  const text = fmtQtyIn(value, unit);
  return displayUnit(unit) === 'ft' ? `${text} ft` : text;
}

/** "1 box", "12 boxes", "42,000 ft". Wire reads in feet, never in C. */
export function fmtUnits(value: number, unit: string): string {
  const shown = displayQty(value, unit);
  const label = displayUnit(unit);
  const text =
    INVARIABLE_UNITS.has(unit) || Math.abs(shown) === 1
      ? label
      : label.endsWith('x')
        ? `${label}es`
        : `${label}s`;
  return `${fmtQty(shown)} ${text}`;
}
